/**
 * Latest Insights Carousel
 * Client-side pagination for latest insights section
 * Responsive: 1 post on mobile, 2 on sm, 3 on lg+
 */

export default class LatestCarousel {
  constructor(element) {
    this.carousel = element;
    this.currentPage = 1;
    this.totalPosts = parseInt(this.carousel.dataset.totalPosts, 10) || 0;

    // Get elements
    this.slides = this.carousel.querySelectorAll('[data-carousel-item]');
    this.prevBtn = this.carousel.querySelector('[data-carousel-prev]');
    this.nextBtn = this.carousel.querySelector('[data-carousel-next]');
    this.pagination = this.carousel.querySelector('[data-carousel-pagination]');

    this.init();
  }

  init() {
    // Calculate initial posts per page based on screen size
    this.calculatePostsPerPage();

    if (this.totalPages <= 1) {
      this.hidePagination();
      return;
    }

    // Bind event listeners
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goToPage(this.currentPage - 1));
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goToPage(this.currentPage + 1));
    }

    // Handle window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const oldPostsPerPage = this.postsPerPage;
        this.calculatePostsPerPage();

        // If posts per page changed, recalculate everything
        if (oldPostsPerPage !== this.postsPerPage) {
          this.currentPage = 1; // Reset to first page
          this.updatePagination();
          this.updateCarousel();
        }
      }, 250);
    });

    // Initialize first page
    this.updatePagination();
    this.updateCarousel();
  }

  /**
   * Calculate posts per page based on screen size
   * Mobile: 1, SM (576px+): 2, LG (992px+): 3
   */
  calculatePostsPerPage() {
    const width = window.innerWidth;
    if (width >= 992) {
      this.postsPerPage = 3;
    } else if (width >= 576) {
      this.postsPerPage = 2;
    } else {
      this.postsPerPage = 1;
    }
    this.totalPages = Math.ceil(this.totalPosts / this.postsPerPage);
  }

  /**
   * Update pagination dots dynamically
   */
  updatePagination() {
    if (!this.pagination) return;

    // Clear existing dots
    this.pagination.innerHTML = '';

    // Show/hide pagination controls based on whether pagination is needed
    if (this.totalPages <= 1) {
      this.hidePagination();
      return;
    } else {
      this.showPagination();
    }

    // Create new dots
    for (let i = 1; i <= this.totalPages; i++) {
      const dot = document.createElement('button');
      dot.className = `pagination-dot ${i === this.currentPage ? 'active' : ''}`;
      dot.setAttribute('data-carousel-page', i);
      dot.setAttribute('aria-label', `Page ${i}`);
      dot.setAttribute('aria-current', i === this.currentPage ? 'page' : 'false');
      dot.innerHTML = `<span class="sr-only">${i}</span>`;

      dot.addEventListener('click', () => {
        this.goToPage(i);
      });

      this.pagination.appendChild(dot);
    }
  }

  /**
   * Hide pagination controls
   */
  hidePagination() {
    if (this.prevBtn) this.prevBtn.style.display = 'none';
    if (this.nextBtn) this.nextBtn.style.display = 'none';
    if (this.pagination) this.pagination.parentElement.style.display = 'none';
  }

  /**
   * Show pagination controls
   */
  showPagination() {
    if (this.prevBtn) this.prevBtn.style.display = '';
    if (this.nextBtn) this.nextBtn.style.display = '';
    if (this.pagination) this.pagination.parentElement.style.display = '';
  }

  goToPage(pageNumber) {
    if (pageNumber < 1 || pageNumber > this.totalPages || pageNumber === this.currentPage) {
      return;
    }

    this.currentPage = pageNumber;
    this.updateCarousel();
    this.updatePaginationState();
  }

  updateCarousel() {
    // Calculate which slides should be visible based on current page and posts per page
    const startIndex = (this.currentPage - 1) * this.postsPerPage;
    const endIndex = startIndex + this.postsPerPage;

    // Hide all slides first
    this.slides.forEach((slide, index) => {
      if (index >= startIndex && index < endIndex) {
        slide.style.display = '';
      } else {
        slide.style.display = 'none';
      }
    });

    // Update navigation buttons
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentPage === 1;
      this.prevBtn.classList.toggle('disabled', this.currentPage === 1);
    }

    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentPage === this.totalPages;
      this.nextBtn.classList.toggle('disabled', this.currentPage === this.totalPages);
    }

    // Scroll to top of carousel smoothly
    const carouselTop = this.carousel.offsetTop - 100;
    if (window.pageYOffset > carouselTop) {
      window.scrollTo({
        top: carouselTop,
        behavior: 'smooth',
      });
    }
  }

  /**
   * Update the active state of pagination dots
   */
  updatePaginationState() {
    if (!this.pagination) return;

    const dots = this.pagination.querySelectorAll('[data-carousel-page]');
    dots.forEach(dot => {
      const dotPage = parseInt(dot.dataset.carouselPage, 10);
      const isActive = dotPage === this.currentPage;

      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-current', isActive ? 'page' : 'false');
    });
  }
}



// WEBPACK FOOTER //
// ./scripts/components/latest-carousel.js